// Client for the rooms API (api/rooms.php) — admin-manageable bedroom/
// suite showcase content for Rooms.tsx and RoomDetail.tsx.
import type { AmenityGroup } from '../components/ui/AmenityList';
import { resolveAmenityIcon } from './iconMap';
import type { PageBlock } from './customPagesApi';

import { parseJsonResponse } from './apiFetch';
const API_BASE: string = import.meta.env.VITE_API_BASE ?? '/api';

export interface AttachedSpace {
  title: string;
  photoLabel: string;
  description: string;
  link?: string;
  confirmLeave?: boolean;
}

export type RoomType = 'bedroom' | 'bathroom' | 'kitchen' | 'living_room' | 'balcony' | 'other';

export const ROOM_TYPE_LABELS: Record<RoomType, string> = {
  bedroom: 'Bedroom',
  bathroom: 'Bathroom',
  kitchen: 'Kitchen',
  living_room: 'Living Room',
  balcony: 'Balcony',
  other: 'Other',
};

export interface GalleryPhotoData {
  label: string;
  shortLabel: string;
}

export interface AmenityItemData {
  icon: string;
  label: string;
}

export interface AmenityGroupData {
  category: string;
  items: AmenityItemData[];
}

export interface Room {
  id: number;
  slug: string;
  room_type: RoomType;
  name: string;
  short_name: string;
  eyebrow: string;
  tagline: string;
  bed_config: string;
  photo_height: number;
  blocks: PageBlock[];
  tags: string[];
  gallery: GalleryPhotoData[];
  amenityGroups: AmenityGroupData[];
  attached: AttachedSpace[];
  sort_order: number;
  is_visible: 0 | 1;
}

export interface RoomInput {
  slug: string;
  room_type: RoomType;
  name: string;
  short_name: string;
  eyebrow: string;
  tagline: string;
  bed_config: string;
  photo_height: number;
  blocks: PageBlock[];
  tags: string[];
  gallery: GalleryPhotoData[];
  amenityGroups: AmenityGroupData[];
  attached: AttachedSpace[];
  sort_order: number;
}

export function roomImageSlotKey(slug: string): string {
  return `rooms.${slug}.main`;
}

export function roomGallerySlotKey(slug: string, index: number): string {
  return `rooms.${slug}.gallery.${index}`;
}

export function roomAttachedSlotKey(slug: string, index: number): string {
  return `rooms.${slug}.attached.${index}`;
}

export function roomVideoSlotKey(slug: string): string {
  return `rooms.${slug}.video`;
}

/** A short plain-text teaser for room-listing cards — the first paragraph
 * block's text, falling back to the first block of any type. */
export function roomTeaser(room: Room): string {
  const paragraph = room.blocks.find((b) => b.type === 'paragraph' && b.content.trim());
  if (paragraph) return paragraph.content;
  return room.blocks.find((b) => b.content.trim())?.content ?? '';
}

/** Resolves the string icon names in amenityGroups to actual lucide-react
 * components for use with the existing <AmenityList> component. */
export function toAmenityGroups(groups: AmenityGroupData[]): AmenityGroup[] {
  return groups.map((g) => ({
    category: g.category,
    items: g.items.map((i) => ({ icon: resolveAmenityIcon(i.icon), label: i.label })),
  }));
}

export async function fetchRooms(bustCache = false): Promise<Room[]> {
  // bustCache: the backend caches this list for 60s for the guest site's
  // benefit, but the admin panel needs to see its own writes (e.g. the
  // visibility toggle) reflected immediately — same pattern as
  // fetchMediaSlots() in mediaApi.ts.
  const qs = bustCache ? `&v=${Date.now()}` : '';
  const res = await fetch(`${API_BASE}/rooms.php?action=list${qs}`);
  const data = await parseJsonResponse(res);
  if (!data.success) throw new Error(data.message ?? 'Failed to load rooms');
  return data.rooms;
}

export async function addRoom(input: RoomInput, csrfToken: string): Promise<{ success: boolean; id?: number; slug?: string; message?: string }> {
  const res = await fetch(`${API_BASE}/rooms.php?action=add`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ ...input, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function updateRoom(id: number, input: RoomInput, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/rooms.php?action=update`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, ...input, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function setRoomVisible(id: number, visible: boolean, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/rooms.php?action=set_visible`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, is_visible: visible, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}

export async function deleteRoom(id: number, csrfToken: string): Promise<{ success: boolean; message?: string }> {
  const res = await fetch(`${API_BASE}/rooms.php?action=delete`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ id, csrf_token: csrfToken }),
  });
  return parseJsonResponse(res);
}
